import { useEffect } from 'react';

interface OrganizationData {
  type: 'Organization';
}

interface ArticleData {
  type: 'Article';
  title: string;
  description: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  slug: string;
}

interface ServiceData {
  type: 'Service';
  name: string;
  description: string;
}

interface LocalBusinessData {
  type: 'LocalBusiness';
}

type JsonLdProps = OrganizationData | ArticleData | ServiceData | LocalBusinessData;

const BASE_URL = 'https://www.tecnosync.com';
const LOGO_URL = 'https://carpeta.tecnosync.com/uploads/68596eb4b0ba6_1750691508.webp';

const getStructuredData = (props: JsonLdProps) => {
  switch (props.type) {
    case 'Organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'TECNOSYNC',
        url: BASE_URL,
        logo: LOGO_URL,
        description: 'Agencia tecnológica innovadora con más de 5 años de experiencia. Desarrollo web, apps móviles, diseño gráfico y marketing digital.',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Punta Cana',
          addressCountry: 'DO',
        },
        sameAs: [
          'https://www.facebook.com/tecnosync',
          'https://www.instagram.com/tecnosync',
          'https://www.linkedin.com/company/tecnosync',
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          availableLanguage: ['Spanish', 'English'],
        },
      };

    case 'Article':
      return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: props.title,
        description: props.description,
        image: props.image || LOGO_URL,
        datePublished: props.datePublished,
        dateModified: props.dateModified || props.datePublished,
        url: `${BASE_URL}/blog/${props.slug}`,
        author: {
          '@type': 'Organization',
          name: 'TECNOSYNC',
          url: BASE_URL,
        },
        publisher: {
          '@type': 'Organization',
          name: 'TECNOSYNC',
          logo: { '@type': 'ImageObject', url: LOGO_URL },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${BASE_URL}/blog/${props.slug}`,
        },
      };

    case 'Service':
      return {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: props.name,
        description: props.description,
        provider: {
          '@type': 'Organization',
          name: 'TECNOSYNC',
          url: BASE_URL,
        },
        areaServed: {
          '@type': 'Country',
          name: 'Dominican Republic',
        },
      };

    case 'LocalBusiness':
      return {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'TECNOSYNC',
        url: BASE_URL,
        logo: LOGO_URL,
        image: LOGO_URL,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Punta Cana',
          addressRegion: 'La Altagracia',
          addressCountry: 'DO',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 18.5601,
          longitude: -68.3725,
        },
        priceRange: '$$',
      };
  }
};

const JsonLd = (props: JsonLdProps) => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-jsonld', props.type);
    script.textContent = JSON.stringify(getStructuredData(props));
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [props]);

  return null;
};

export default JsonLd;
